import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import DataGrid, { Column, DataGridTypes, GroupPanel, Lookup, Paging, Selection } from 'devextreme-react/data-grid';
import Form, { GroupItem, Label, SimpleItem } from "devextreme-react/form";
import './gestion.activites.css';
import { Button } from 'devextreme-react';
import RadioGroup from 'devextreme-react/radio-group';
import { FieldDataChangedEvent } from 'devextreme/ui/form';
import IActivite from "../../models/IActivite";
import IGestionActivite from "../../models/IGestionActivite";
import { Mode } from "../../hooks/useCustomDataSource";
import useLookUpBase from '../../components/selectbox/LookupBase';
import Guid from "../../models/Guid";
import ILookupItem from '../../models/ILookupItem';
import IActiviteSecteur from '../../models/IActiviteSecteur';

const estLookupValide = (item: ILookupItem) =>
  !item.id.isEmpty() && item.description?.trim().toLowerCase() !== "aucun";

const secteursDisponibilite = [
  { text: "Tous les secteurs sont disponibles pour l'activité" },
  { text: "Limiter les secteurs pour l'activité" }
];

interface LookupSelection {
  id: string;
  description?: string;
}

export interface IGestionActiviteSecteursProps {
  mode: Mode;
  selectedActivite: IGestionActivite;
  setSelectedDataRow: React.Dispatch<React.SetStateAction<IGestionActivite>>;
  setIsFieldModifier: (value: boolean) => void;
  onFieldDataChanged: (e: FieldDataChangedEvent) => void;
}

export default function GestionActiviteSecteurs({
  mode,
  selectedActivite,
  setSelectedDataRow,
  setIsFieldModifier,
  onFieldDataChanged,
}: IGestionActiviteSecteursProps) {
  const { editorOptions: secteursEditorOptions, data: tousLesSecteurs } = useLookUpBase({
    isModeModifier: mode !== Mode.Lecture,
    controllerName: "secteur",
    showClear: mode !== Mode.Lecture,
    setDataRow: () => { },
    includeEmpty: false
  });

  const [radioSecteurs, setRadioSecteurs] = useState<string>(() =>
    selectedActivite?.activiteSecteurs?.some((item) => !item.isDeleted && !item.idSecteur.isEmpty())
      ? secteursDisponibilite[1].text
      : secteursDisponibilite[0].text
  );
  const [selectedSecteur, setSelectedSecteur] = useState<LookupSelection>();
  const [selectedDispoSecteur, setSelectedDispoSecteur] = useState<(IActiviteSecteur & { description?: string })>();
  const [isAddingSecteur, setIsAddingSecteur] = useState(false);
  const skipRadioDirtyRef = useRef(false);

  const activiteSecteurs = useMemo(
    () => selectedActivite?.activiteSecteurs ?? [],
    [selectedActivite?.activiteSecteurs]
  );

  const catalogueSecteurs = useMemo(
    () => (tousLesSecteurs ?? []).filter(estLookupValide),
    [tousLesSecteurs]
  );

  const secteursEditorOptionsFiltres = useMemo(
    () => ({ ...secteursEditorOptions, dataSource: catalogueSecteurs }),
    [secteursEditorOptions, catalogueSecteurs]
  );

  const activiteSecteursAffichage = useMemo(
    () => activiteSecteurs
      .filter((item) => !item.isDeleted && !item.idSecteur.isEmpty())
      .map((item) => ({
        ...item,
        description: catalogueSecteurs.find((s) => s.id.equals(item.idSecteur))?.description ?? ""
      })),
    [activiteSecteurs, catalogueSecteurs]
  );

  const secteursDisponiblesAjout = useMemo(
    () => catalogueSecteurs.filter(
      (secteur) => !activiteSecteurs.some((item) => item.idSecteur.equals(secteur.id) && !item.isDeleted)
    ),
    [catalogueSecteurs, activiteSecteurs]
  );

  const secteursEnEdition = mode !== Mode.Lecture && radioSecteurs === secteursDisponibilite[1].text;

  const onSelectionChangedSecteurs = useCallback(({ selectedRowsData }: DataGridTypes.SelectionChangedEvent) => {
    if (mode === Mode.Lecture) return
    setSelectedSecteur(selectedRowsData[0]);
  }, [mode]);

  const onSelectionChangedDispoSecteurs = useCallback(({ selectedRowsData }: DataGridTypes.SelectionChangedEvent) => {
    if (mode === Mode.Lecture) return
    setSelectedDispoSecteur(selectedRowsData[0]);
  }, [mode]);

  const add = () => {
    if (!selectedSecteur || !selectedActivite?.idActivite || isAddingSecteur
      || new Guid(selectedSecteur.id).isEmpty() || selectedSecteur.description?.trim().toLowerCase() === "aucun")
      return
    setIsAddingSecteur(true);
    skipRadioDirtyRef.current = true;
    setRadioSecteurs(secteursDisponibilite[1].text);
    skipRadioDirtyRef.current = false;

    setSelectedDataRow((prev: IGestionActivite) => {
      const secteurs = prev.activiteSecteurs ?? [];
      const existingIndex = secteurs.findIndex((item) =>
        item.idSecteur.toString() === selectedSecteur.id
      );

      if (existingIndex !== -1) {
        const updated = [...secteurs];
        updated[existingIndex] = { ...updated[existingIndex], isDeleted: false };
        return { ...prev, activiteSecteurs: updated };
      }

      return {
        ...prev,
        activiteSecteurs: [...secteurs, {
          idActivite: selectedActivite.idActivite,
          idSecteur: new Guid(selectedSecteur.id),
          isDeleted: false
        } as IActiviteSecteur]
      };
    });

    setIsFieldModifier(true);
  }

  useEffect(() => {
    if (isAddingSecteur) {
      const timer = setTimeout(() => setIsAddingSecteur(false), 300);
      return () => clearTimeout(timer);
    }
    return;
  }, [isAddingSecteur]);

  const addAll = () => {
    if (!catalogueSecteurs.length || !selectedActivite?.idActivite || isAddingSecteur) return

    setIsAddingSecteur(true);
    skipRadioDirtyRef.current = true;
    setRadioSecteurs(secteursDisponibilite[1].text);
    skipRadioDirtyRef.current = false;

    setSelectedDataRow((prev: IGestionActivite) => {
      const updatedSecteurs = [...(prev.activiteSecteurs ?? [])];

      catalogueSecteurs.forEach(secteur => {
        const existingIndex = updatedSecteurs.findIndex((item) =>
          item.idSecteur.equals(secteur.id)
        );

        if (existingIndex !== -1) {
          updatedSecteurs[existingIndex] = {
            ...updatedSecteurs[existingIndex],
            isDeleted: false
          };
        } else {
          updatedSecteurs.push({
            idActivite: selectedActivite.idActivite,
            idSecteur: secteur.id,
            isDeleted: false
          } as IActiviteSecteur);
        }
      });

      return { ...prev, activiteSecteurs: updatedSecteurs };
    });

    setIsFieldModifier(true);
  }

  const remove = () => {
    if (!selectedDispoSecteur || !activiteSecteurs.length || isAddingSecteur)
      return

    setIsAddingSecteur(true);

    setSelectedDataRow((prev: IGestionActivite) => {
      const secteurs = prev.activiteSecteurs ?? [];
      const index = secteurs.findIndex(item =>
        item.idSecteur.equals(selectedDispoSecteur.idSecteur)
      );
      if (index === -1) return prev;

      const updatedData = [...secteurs];
      updatedData[index] = { ...updatedData[index], isDeleted: true };
      return { ...prev, activiteSecteurs: updatedData };
    });
    setIsFieldModifier(true);
  }

  const removeAll = () => {
    if (!activiteSecteurs.length || isAddingSecteur)
      return

    setIsAddingSecteur(true);

    setSelectedDataRow((prev: IGestionActivite) => ({
      ...prev,
      activiteSecteurs: (prev.activiteSecteurs ?? []).map((item) => ({
        ...item,
        isDeleted: true
      }))
    }));
    setIsFieldModifier(true);
  }

  const onValueChangedSecteurs = useCallback((e: { value?: string; event?: unknown }) => {
    if (e.value == null) return;

    setRadioSecteurs(e.value);

    if (e.value === secteursDisponibilite[0].text) {
      setSelectedSecteur(undefined);
      setSelectedDispoSecteur(undefined);
      setSelectedDataRow((prev: IGestionActivite) => ({
        ...prev,
        activiteSecteurs: (prev.activiteSecteurs ?? []).map((item) => ({
          ...item,
          isDeleted: true
        }))
      }));
    }

    if (!skipRadioDirtyRef.current && e.event != null)
      setIsFieldModifier(true);
  }, [setSelectedDataRow, setIsFieldModifier]);

  useEffect(() => {
    const hasActiveSecteurs = activiteSecteurs.some((item) => !item.isDeleted && !item.idSecteur.isEmpty());
    skipRadioDirtyRef.current = true;
    setRadioSecteurs(hasActiveSecteurs ? secteursDisponibilite[1].text : secteursDisponibilite[0].text);
    skipRadioDirtyRef.current = false;
  }, [selectedActivite?.idActivite]);

  return (
    <Form readOnly={mode === Mode.Lecture} alignItemLabelsInAllGroups={true} formData={selectedActivite || {} as IActivite} className={"forms"} labelLocation={"left"} showColonAfterLabel={true} onFieldDataChanged={onFieldDataChanged}>
      <GroupItem>
        <GroupItem>
          <RadioGroup
            disabled={mode === Mode.Lecture}
            dataSource={secteursDisponibilite}
            value={radioSecteurs}
            onValueChanged={onValueChangedSecteurs}
            displayExpr="text"
            valueExpr="text"
          />
        </GroupItem>
        <GroupItem caption={"Secteurs disponible pour cet activité"}>
        </GroupItem>
        <GroupItem colCount={3} cssClass="activite-liens-dual-list">
          <GroupItem>
            <DataGrid
              disabled={!secteursEnEdition}
              showBorders={true}
              dataSource={activiteSecteursAffichage}
              allowColumnReordering={false}
              keyExpr="idSecteur"
              onSelectionChanged={onSelectionChangedDispoSecteurs}
            >
              <Selection mode="single" />
              <GroupPanel visible={mode === Mode.Lecture} />
              <Column dataField="description" alignment="left" />
              <Column dataField="isDeleted" dataType="boolean" visible={false} />
            </DataGrid>
          </GroupItem>
          <SimpleItem
            cssClass="activite-liens-transfer-item"
            render={() => (
              <div className="activite-liens-transfer">
                <div className="activite-liens-transfer__row">
                  <Button text={"<"} disabled={!secteursEnEdition || !selectedSecteur || isAddingSecteur} onClick={add} />
                  <Button text={">"} disabled={!secteursEnEdition || !selectedDispoSecteur || isAddingSecteur} onClick={remove} />
                </div>
                <div className="activite-liens-transfer__row">
                  <Button text={"<<"} disabled={!secteursEnEdition || isAddingSecteur} onClick={addAll} />
                  <Button text={">>"} disabled={!secteursEnEdition || isAddingSecteur} onClick={removeAll} />
                </div>
              </div>
            )}
          >
            <Label visible={false} />
          </SimpleItem>
          <GroupItem>
            <DataGrid
              disabled={!secteursEnEdition}
              showBorders={true}
              dataSource={secteursDisponiblesAjout}
              allowColumnReordering={false}
              keyExpr="id"
              onSelectionChanged={onSelectionChangedSecteurs}
            >
              <Selection mode="single" />
              <GroupPanel visible={mode === Mode.Lecture} />
              <Paging defaultPageSize={10} />
              <Column dataField="id" alignment="left">
                <Lookup {...secteursEditorOptionsFiltres} />
              </Column>
            </DataGrid>
          </GroupItem>
        </GroupItem>
      </GroupItem>
    </Form>
  );
}
